import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarCheck,
  CalendarClock,
  ChevronRight,
  ClipboardCheck,
  FileCheck,
  Hourglass,
  AlertCircle,
  ShieldAlert
} from 'lucide-react';
import clsx from 'clsx';
import { buildAssessorDashboardRoutes } from '@utils/assessorDashboardRoutes';
import type { AssessorQuickActionsData } from '@app-types';
import styles from './ProgramLeadQuickActions.module.css';
import tones from './AssessorQuickActions.module.css';

interface AssessorQuickActionsProps {
  data: AssessorQuickActionsData | undefined;
  isLoading: boolean;
  userId: number;
}

const AssessorQuickActions: React.FC<AssessorQuickActionsProps> = ({ data, isLoading, userId }) => {
  const navigate = useNavigate();
  const routes = data ? buildAssessorDashboardRoutes(data, userId) : undefined;

  const actions = [
    { label: 'Meetings in Conflict', count: data?.meetings_in_conflict, icon: AlertTriangle, tone: tones.iconDanger, route: routes?.meetingsInConflict },
    { label: 'Meetings Pending Reschedule', count: data?.meetings_pending_reschedule, icon: CalendarClock, tone: tones.iconDanger, route: routes?.meetingsPendingReschedule },
    { label: 'Scheduled Meetings', count: data?.scheduled_meetings, icon: CalendarCheck, tone: tones.iconInfo, route: routes?.scheduledMeetings },
    { label: 'Assessments Pending Publishing', count: data?.assessments_pending_publishing, icon: FileCheck, tone: tones.iconWarning, route: routes?.assessmentsPendingPublishing },
    {
      label: 'Evidence Pending Review',
      hint: 'Evidence submitted and awaiting your review',
      count: data?.evidence_pending_review,
      icon: ClipboardCheck,
      tone: tones.iconWarning,
      route: routes?.evidencePendingReview
    },
    { label: 'Assessments Due Today/Tomorrow', count: data?.assessments_due_soon, icon: Hourglass, tone: tones.iconWarning, route: routes?.assessmentsDueSoon },
    { label: 'Assessments Past Due', count: data?.assessments_past_due, icon: AlertCircle, tone: tones.iconDanger, route: routes?.assessmentsPastDue },
    { label: 'Assessments Escalated', count: data?.assessments_escalated, icon: ShieldAlert, tone: tones.iconDanger, route: routes?.assessmentsEscalated }
  ];

  return (
    <div className={styles.quickActionsContainer}>
      <div className={styles.actionsGrid}>
        {actions.map((action) => {
          const IconComponent = action.icon;
          return (
            <button
              type="button"
              key={`action-${action.label}`}
              className={clsx(styles.actionTile, tones.tile)}
              disabled={!action.route}
              onClick={() => action.route && navigate(action.route)}
            >
              <div className={clsx(styles.actionIcon, action.tone)}>
                <IconComponent size={24} />
              </div>
              <div className={styles.actionContent}>
                <p className={styles.actionLabel}>{action.label}</p>
                <p className={styles.actionCount}>{isLoading ? '–' : (action.count ?? 0)}</p>
                {action.hint && <p className={tones.hint}>{action.hint}</p>}
              </div>
              <ChevronRight size={20} className={styles.actionArrow} />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export { AssessorQuickActions };
